import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useRef, useEffect } from "react";
import { DayPicker } from "react-day-picker";
import { addDays, addMonths, format, isBefore, isSameDay, eachDayOfInterval } from "date-fns";
import { es, fr, de, it, ptBR, enGB } from "react-day-picker/locale";
import { formatDateLocale } from "../../../../infrastructure/utils/formatDateLocale";
// Map app locale codes to date-fns/react-day-picker locale objects
const localeMap = {
    es: es,
    "es-ES": es,
    fr: fr,
    de: de,
    it: it,
    "pt-BR": ptBR,
    "en-GB": enGB,
};
// Weekday names per locale (short, starting Monday)
const weekDaysByLocale = {
    en: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    es: ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"],
    "es-ES": ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"],
    fr: ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"],
    de: ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"],
    it: ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"],
    "pt-BR": ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"],
    "en-GB": ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
};
const DateRangePicker = ({ startDate, endDate, setStartDate, setEndDate, setShowDateModal, setFooterHtml, formatDate, t, setTrigger, }) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Resolve locale
    const appLocale = t("locale") || "en";
    const rdpLocale = localeMap[appLocale] || undefined;
    const weekDays = weekDaysByLocale[appLocale] || weekDaysByLocale["en"];
    const parseInitialDate = (dateStr) => {
        if (!dateStr)
            return undefined;
        // Parse as local date (not UTC). new Date("YYYY-MM-DD") parses as UTC
        // midnight, which in negative UTC offsets becomes the previous day.
        const [year, month, day] = dateStr.split("-").map(Number);
        if (!year || !month || !day)
            return undefined;
        return new Date(year, month - 1, day);
    };
    const initialFrom = parseInitialDate(startDate[1]);
    const initialTo = parseInitialDate(endDate[1]);
    const [pickupDate, setPickupDate] = useState(initialFrom);
    const [dropoffDate, setDropoffDate] = useState(initialTo);
    const [step, setStep] = useState("pickup");
    const scrollContainerRef = useRef(null);
    const scrollPositionRef = useRef(0);
    // Scroll to selected start date on mount
    useEffect(() => {
        const targetDate = pickupDate || today;
        if (scrollContainerRef.current) {
            const timer = setTimeout(() => {
                var _a;
                const targetMonth = targetDate.getMonth();
                const targetYear = targetDate.getFullYear();
                const monthElements = (_a = scrollContainerRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll(".rdp-month");
                if (monthElements) {
                    for (const el of Array.from(monthElements)) {
                        const caption = el.querySelector(".rdp-month_caption");
                        if (caption) {
                            const text = caption.textContent || "";
                            const monthDate = new Date(`${text} 1`);
                            if (monthDate.getMonth() === targetMonth && monthDate.getFullYear() === targetYear) {
                                el.scrollIntoView({ behavior: "auto", block: "start" });
                                break;
                            }
                        }
                    }
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, []);
    // Save scroll position before state changes to prevent jump on re-render
    const saveScrollPosition = useCallback(() => {
        if (scrollContainerRef.current) {
            scrollPositionRef.current = scrollContainerRef.current.scrollTop;
        }
    }, []);
    // Restore scroll position after re-render
    useEffect(() => {
        if (scrollContainerRef.current && scrollPositionRef.current > 0) {
            scrollContainerRef.current.scrollTop = scrollPositionRef.current;
        }
    }, [pickupDate, dropoffDate]);
    // Handle day click — manual 2-step selection like Litepicker
    const handleDayClick = useCallback((day) => {
        if (isBefore(day, today) || isSameDay(day, today))
            return;
        // Save scroll position before any state change
        saveScrollPosition();
        if (step === "pickup") {
            // First click: set pickup, clear dropoff
            setPickupDate(day);
            setDropoffDate(undefined);
            setStep("dropoff");
        }
        else {
            // Second click: set dropoff
            if (isSameDay(day, pickupDate)) {
                // Clicked same day as pickup — reset, wait for new dropoff
                setPickupDate(day);
                setDropoffDate(undefined);
                setStep("dropoff");
            }
            else if (isBefore(day, pickupDate)) {
                // Clicked before pickup — swap: clicked becomes pickup, old pickup becomes dropoff
                setDropoffDate(pickupDate);
                setPickupDate(day);
                setStep("pickup");
            }
            else {
                // Clicked after pickup — normal: set as dropoff
                setDropoffDate(day);
                setStep("pickup");
            }
        }
    }, [step, pickupDate, today, saveScrollPosition]);
    // Build modifiers for visual styling
    const modifiers = {
        rangeStart: pickupDate ? [pickupDate] : [],
        rangeEnd: dropoffDate ? [dropoffDate] : [],
        rangeMiddle: pickupDate && dropoffDate
            ? eachDayOfInterval({ start: pickupDate, end: dropoffDate }).filter((d) => !isSameDay(d, pickupDate) && !isSameDay(d, dropoffDate))
            : [],
        singleSelected: pickupDate && !dropoffDate ? [pickupDate] : [],
    };
    const modifiersClassNames = {
        rangeStart: "rdp-range_start",
        rangeEnd: "rdp-range_end",
        rangeMiddle: "rdp-range_middle",
        singleSelected: "rdp-single-selected",
    };
    const restoreMobileUI = useCallback(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        (_a = document.querySelector(".header")) === null || _a === void 0 ? void 0 : _a.classList.remove("hidden");
        document.body.classList.remove("noscroll");
        (_b = document.querySelector(".search_bg")) === null || _b === void 0 ? void 0 : _b.classList.remove("open");
        (_c = document.querySelector("#chat")) === null || _c === void 0 ? void 0 : _c.classList.remove("hidden");
        (_d = document.querySelector(".searchbar")) === null || _d === void 0 ? void 0 : _d.classList.remove("hidden");
        (_e = document.getElementsByClassName("shopify-section-group-header-group")[0]) === null || _e === void 0 ? void 0 : _e.classList.remove("hidden");
        (_f = document.getElementsByClassName("header-wrapper")[0]) === null || _f === void 0 ? void 0 : _f.classList.remove("hidden");
        (_g = document.querySelector(".sticky_main_div")) === null || _g === void 0 ? void 0 : _g.classList.remove("hidden");
        (_h = document.querySelector(".logolistfix")) === null || _h === void 0 ? void 0 : _h.classList.remove("hidden");
        (_j = document.querySelector("#help_label_chat")) === null || _j === void 0 ? void 0 : _j.classList.remove("hidden");
        (_k = document.querySelector(".right_side_logo")) === null || _k === void 0 ? void 0 : _k.classList.remove("hidden");
        (_l = document.querySelector(".banner__content")) === null || _l === void 0 ? void 0 : _l.classList.remove("hidden");
    }, []);
    // Listen for BlueBar closing the modal — restore elements that initMobileMode hid
    useEffect(() => {
        const blueBar = document.querySelector("#date-picker-modal #blue-bar-reservation .main-back-blue-bar");
        if (!blueBar)
            return;
        const handleBlueBarClose = () => {
            setTimeout(() => restoreMobileUI(), 50);
        };
        blueBar.addEventListener("click", handleBlueBarClose);
        return () => blueBar.removeEventListener("click", handleBlueBarClose);
    }, [restoreMobileUI]);
    const handleConfirm = useCallback(() => {
        if (pickupDate && dropoffDate) {
            const fromFormatted = formatDate(pickupDate.toISOString());
            const fromISO = format(pickupDate, "yyyy-MM-dd");
            const toFormatted = formatDate(dropoffDate.toISOString());
            const toISO = format(dropoffDate, "yyyy-MM-dd");
            setStartDate([fromFormatted, fromISO]);
            setEndDate([toFormatted, toISO]);
            setFooterHtml(`
        <span class="js-start-date start-date">${fromFormatted}</span>
        <span> - </span>
        <span class="js-end-date end-date" id="endDateBar">${toFormatted}</span>
      `);
            const startInput = document.getElementById("start");
            const endInput = document.getElementById("end");
            if (startInput) {
                startInput.value = fromFormatted;
                startInput.classList.add("selected");
            }
            if (endInput) {
                endInput.value = toFormatted;
                endInput.classList.add("selected");
            }
            const startDateSpan = document.querySelector(".js-start-date");
            const endDateSpan = document.querySelector(".js-end-date");
            if (startDateSpan) {
                startDateSpan.textContent = fromFormatted;
                startDateSpan.innerHTML = fromFormatted;
            }
            if (endDateSpan) {
                endDateSpan.textContent = toFormatted;
                endDateSpan.innerHTML = toFormatted;
            }
            if (sessionStorage.getItem("preloadSearchStarted") === "true") {
                setTrigger(Math.floor(Math.random() * 5000) + 1);
            }
            // Close the modal through ModalPopUpTarget's BlueBar (same path as back button)
            const blueBarBtn = document.querySelector("#date-picker-modal #blue-bar-reservation .main-back-blue-bar");
            if (blueBarBtn) {
                blueBarBtn.click();
            }
            else {
                setShowDateModal(false);
                setTimeout(() => restoreMobileUI(), 50);
            }
        }
    }, [pickupDate, dropoffDate, formatDate, setStartDate, setEndDate, setFooterHtml, setTrigger, setShowDateModal, restoreMobileUI]);
    // i18n footer text
    const getFooterText = () => {
        if (!pickupDate)
            return t("select_first_date") || "Select The Pick-Up Date";
        if (!dropoffDate)
            return t("select_second_date") || "Select The Drop-Off Date";
        const fromLabel = formatDateLocale(pickupDate, appLocale);
        const toLabel = formatDateLocale(dropoffDate, appLocale);
        return `${fromLabel} - ${toLabel}`;
    };
    const isConfirmDisabled = !pickupDate || !dropoffDate;
    return (_jsxs("div", { className: "date-range-picker", children: [_jsx("div", { className: "date-range-picker__weekdays", children: weekDays.map((day) => (_jsx("span", { className: "date-range-picker__weekday", children: day }, day))) }), _jsx("div", { className: "date-range-picker__calendar", ref: scrollContainerRef, children: _jsx(DayPicker, { numberOfMonths: 15, defaultMonth: today, startMonth: today, endMonth: addMonths(today, 14), onDayClick: handleDayClick, disabled: { before: addDays(today, 1) }, weekStartsOn: 1, showOutsideDays: false, fixedWeeks: false, modifiers: modifiers, modifiersClassNames: modifiersClassNames, locale: rdpLocale, formatters: {
                        formatDay: (date) => format(date, "dd"),
                    } }) }), _jsxs("div", { className: "date-range-picker__footer", children: [_jsx("span", { className: "date-range-picker__footer-text", children: getFooterText() }), _jsx("button", { type: "button", className: "date-range-picker__confirm", disabled: isConfirmDisabled, onClick: handleConfirm, "aria-label": t("button-apply") || "Apply", children: t("button-apply") || "Apply" })] })] }));
};
export default DateRangePicker;
